import { useEffect, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Search from "../../../../components/Search";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import { setCurrentPage, setSearchText } from "../../../../redux/historicalSlice";
import { RootState } from "../../../../redux/store";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import { useExtraFieldPagination } from "../hooks/useExtraFieldPagination";
import { SurveyImportExtraFieldListItemDto } from "../types";
import AlertMessage from "../../../../components/AlertMessage";
import { useQueuedAlerts } from "../../../../hooks/useQueuedAlerts";
import ModalGlobal from "../../../../components/AppModal/ModalGlobal";
import { useModal } from "../../../../hooks/useModal";
import { removeExtraFieldContent } from "../api/extraFieldApi";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";

export default function ExtraFieldList() {
    const { t } = useTranslation(["common", "extraField"]);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [textToSearch, setTextToSearch] = useState<string>("");
    const location = useLocation();

    const currentPage = useSelector((state: RootState) => state.historical.extraFields.currentPage);
    const itemsPerPage = useSelector((state: RootState) => state.historical.extraFields.itemsPerPage);

    const [showEmpties, setShowEmpties] = useState(false);
    
    const { data: extraFieldsPagination, isLoading, isFetching, error, refetch } = useExtraFieldPagination({ showEmpties });

    const { alert, isVisible, handleCloseAlert, showAlert } = useQueuedAlerts();
    
    const { showModal: showConfirmRemoveContentModal, modalData, openModal: openConfirmRemoveContentModal, closeModal, currentId } = useModal<SurveyImportExtraFieldListItemDto>();
    
    useEffect(() => {
        if (location.state?.shouldRefresh) {
            refetch();
        }
    }, [location.state, refetch]);

    const handleSearchInput = () => {
        dispatch(setSearchText({ table: "extraFields", text: textToSearch }));
        dispatch(setCurrentPage({ table: "extraFields", page: 1 }));
    };

    const handleClear = () => {
        dispatch(setCurrentPage({ table: "extraFields", page: 1 }));
        dispatch(setSearchText({ table: "extraFields", text: "" }));
        setTextToSearch("");
    };

    const handleRemoveExtraFieldContent = (id: number) => {
        closeModal();
        removeExtraFieldContent(id)
            .then(() => {
                refetch();
            })
            .catch((error: ApiErrorResponse) => {
                showAlert({
                    message: error.message ?? t("common:requestError.errorBadRequest"),
                    type: 'danger'
                });
            });
    }

    const columns: ColumnConfig<SurveyImportExtraFieldListItemDto>[] = [
        { header: t("extraField:table.fieldName"), key: "fieldName", render: (item) => <>{t(`extraField:field.${item.fieldName}`)}</> },
        { header: t("extraField:table.description"), key: "description", render: (item) => <>{item.description}</> },
        { header: t("extraField:table.objectType"), key: "objectType", render: (item) => <>{t(`extraField:objectType.${item.objectType}`)}</> },
        { header: t("extraField:table.showInHistorical"), key: "showInHistorical", render: (item) => <>{item.showInHistorical ? t("common:yes") : t("common:no")}</> },
        { header: t("extraField:table.showInDetails"), key: "showInDetails", render: (item) => <>{item.showInDetails ? t("common:yes") : t("common:no")}</> },
        { header: t("extraField:table.isFilterable"), key: "isFilterable", render: (item) => <>{item.isFilterable ? t("common:yes") : t("common:no")}</> },
    ];

    return (
        <>
            <ModalGlobal
                show={showConfirmRemoveContentModal}
                onClose={closeModal}
                subtitle={modalData.subtitle}
                contentType={modalData.type}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                onSave={() => handleRemoveExtraFieldContent(currentId)}
                isLoading={false}
            />
            {isLoading || isFetching && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}

            <Row className="mb-2 align-items-center justify-content-between">
                <Col xs={12} md>
                    <h2>{t("extraField:title")}</h2>
                </Col>
            </Row>

            <Row>
                {isVisible && (
                    <AlertMessage
                        message={alert!.message}
                        type={alert!.type}
                        onClose={handleCloseAlert}
                    />
                )}
            </Row>
            <Row>
                <Col className="text-center" xs={12} md={12}>
                    {error && <div className="alert alert-danger">{t("common:requestError.errorBadRequest")}</div>}
                </Col>
            </Row>

            <Row className="mb-2">
                <Col xs={12}>
                    <Search
                        searchText={textToSearch || ""}
                        onChange={setTextToSearch}
                        onSearch={handleSearchInput}
                        onClear={handleClear}
                    />
                </Col>
            </Row>

            <Row>
                <Form>
                    <Form.Check
                        className="my-2"
                        type="switch"
                        id="extra-fields-show-empties-switch"
                        label={t("extraField:filters.showEmpties")}
                        onChange={(e) => {
                            setShowEmpties(e.target.checked);
                            dispatch(setCurrentPage({ table: "extraFields", page: 1 }));
                        }}
                        checked={showEmpties}
                    />
                </Form>
            </Row>

            <AppTable<SurveyImportExtraFieldListItemDto>
                data={extraFieldsPagination?.items ?? []}
                columns={columns}
                rowStyle={(item) => ({
                    backgroundColor: item.description ? undefined : "#D9D9D9"
                })}
                emptyMessage={t("extraField:emptyMessage")}
                t={t}
                hasActions={true}
                hasPagination={true}
                currentPage={currentPage}
                totalPages={extraFieldsPagination?.totalPages || 0}
                pageSize={itemsPerPage}
                paginationType="extraFields"
                sectionPermission={SectionPermissionTypeEnum.EXTRA_FIELDS}
                onView={(id: number) => {
                    navigate(`/admin/extraFields/${id}`);
                }}
                onDelete={(record) =>
                    openConfirmRemoveContentModal(
                        record,
                        "",
                        t("extraField:modal.removeContentText"),
                        'deleteWithoutInput',
                        ""
                    )
                }
                canDelete={(item) => !!item.description}
            />
        </>
    );
}
